import React, { useEffect, useRef } from "react";
import styles from "../Assessment.module.css";

const BAR_COUNT = 56;
const MIN_BAR_HEIGHT = 4;
const MAX_BAR_HEIGHT = 24;
const SILENCE_THRESHOLD = 12;
const SCROLL_INTERVAL_MS = 130;
const PEAK_SMOOTHING = 0.22;
const HEIGHT_SMOOTHING = 0.14;

interface Props {
  active: boolean;
  analyser?: AnalyserNode | null;
}

const RecordingWaveform = ({ active, analyser }: Props): React.ReactElement => {
  const barRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const heightsRef = useRef<number[]>(Array.from({ length: BAR_COUNT }, () => MIN_BAR_HEIGHT));
  const animationFrameRef = useRef(0);

  useEffect(() => {
    const setBarHeights = (heights: number[]) => {
      heights.forEach((height, index) => {
        const bar = barRefs.current[index];
        if (bar) {
          bar.style.height = `${height}px`;
        }
      });
    };

    const resetBars = () => {
      const idle = Array.from({ length: BAR_COUNT }, () => MIN_BAR_HEIGHT);
      heightsRef.current = idle;
      setBarHeights(idle);
    };

    if (!active || !analyser || analyser.context.state === "closed") {
      resetBars();
      return undefined;
    }

    const timeData = new Uint8Array(analyser.fftSize);
    let cancelled = false;
    let smoothedPeak = 0;
    let lastScrollTime = performance.now();
    let targetHeights = Array.from({ length: BAR_COUNT }, () => MIN_BAR_HEIGHT);
    let displayedHeights = Array.from({ length: BAR_COUNT }, () => MIN_BAR_HEIGHT);

    const peakToHeight = (peak: number): number =>
      peak < SILENCE_THRESHOLD
        ? MIN_BAR_HEIGHT
        : Math.max(MIN_BAR_HEIGHT, Math.min(MAX_BAR_HEIGHT, (peak / 128) * MAX_BAR_HEIGHT * 2.8));

    const tick = () => {
      if (cancelled) return;

      try {
        analyser.getByteTimeDomainData(timeData);

        let peak = 0;
        for (let i = 0; i < timeData.length; i += 1) {
          peak = Math.max(peak, Math.abs(timeData[i] - 128));
        }

        smoothedPeak += (peak - smoothedPeak) * PEAK_SMOOTHING;
        const nextHeight = peakToHeight(smoothedPeak);
        const now = performance.now();

        if (now - lastScrollTime >= SCROLL_INTERVAL_MS) {
          lastScrollTime = now;
          targetHeights = [...targetHeights.slice(1), nextHeight];
        } else {
          targetHeights = [...targetHeights];
          targetHeights[targetHeights.length - 1] = nextHeight;
        }

        displayedHeights = targetHeights.map((target, index) => {
          const current = displayedHeights[index] ?? MIN_BAR_HEIGHT;
          return current + (target - current) * HEIGHT_SMOOTHING;
        });

        heightsRef.current = displayedHeights;
        setBarHeights(displayedHeights);
      } catch {
        cancelled = true;
        return;
      }

      animationFrameRef.current = requestAnimationFrame(tick);
    };

    animationFrameRef.current = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(animationFrameRef.current);
      resetBars();
    };
  }, [active, analyser]);

  return (
    <div className={styles.recordingWaveform} aria-hidden>
      {Array.from({ length: BAR_COUNT }, (_, index) => (
        <span
          key={index}
          ref={(element) => {
            barRefs.current[index] = element;
          }}
          className={styles.recordingWaveformBar}
        />
      ))}
    </div>
  );
};

export default RecordingWaveform;
